import React from 'react';

import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';

import './index.css';

import { type AppConfig, isDev } from './AppConfig';
import reportWebVitals from './reportWebVitals';

Sentry.init({
  allowUrls: [/https:\/\/app\.blankslatetechnologies\.com/i, /https:\/\/app-dev\.blankslatetechnologies\.com/i],
  // replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  // replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  beforeSend(event) {
    const url = window.location.href;
    if (url.includes('app-blankslate.il') || url.includes('localhost')) {
      return null;
    }

    return event;
  },
  dsn: 'https://47a880c1cbc6163edc87a5fc36746620@o4508858350043136.ingest.us.sentry.io/4508930425880576',
  environment: isDev ? 'development' : 'production',
  integrations: [
    Sentry.replayIntegration({
      blockAllMedia: true,
      flushMaxDelay: 2500,
      flushMinDelay: 2000,
      maskAllInputs: true,
      maskAllText: true,
      minReplayDuration: 0,
    }),
  ],
  replaysOnErrorSampleRate: 1.0,
  // Session Replay
  // replaysSessionSampleRate: 0,
  replaysSessionSampleRate: 1.0, //temp solution till we need to record survey cycle data
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    // 'localhost',
    /^https:\/\/svc\.blankslatetech\.co\/sreo/,
    /^https:\/\/svc-dev\.blankslatetech\.co\/sreo/,
  ],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
});

const CONFIG_KEYS = [
  'appUUID',
  'debugHttp',
  'autoSignIn',
  'baseName',
  'apiUrl',
  'redirectUrl',
  'requestTimeout',
  'answerTimeout',
  'headwayAccount',
  'amplitudeApiKey',
] as const satisfies readonly (keyof AppConfig)[];

const validateRuntimeConfig = (raw: unknown): Partial<AppConfig> => {
  if (!raw || typeof raw !== 'object') {
    return {};
  }
  const input = raw as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const key of CONFIG_KEYS) {
    const value = input[key];
    if (value === undefined) {
      continue;
    }
    if ((key === 'apiUrl' || key === 'redirectUrl') && (typeof value !== 'string' || !value.startsWith('https://'))) {
      continue;
    }
    out[key] = value;
  }
  return out as Partial<AppConfig>;
};

const loadRuntimeConfig = async (): Promise<void> => {
  try {
    const res = await fetch('/config.json', { cache: 'no-store' });
    if (!res.ok) {
      return;
    }
    (window as unknown as { __APP_CONFIG__?: Partial<AppConfig> }).__APP_CONFIG__ = validateRuntimeConfig(
      await res.json(),
    );
  } catch {}
};

loadRuntimeConfig().then(async () => {
  const { default: App } = await import('./App');

  const root = createRoot(document.getElementById('root') as HTMLElement);
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );

  reportWebVitals();
});
